import React, { useState, useRef, useEffect } from 'react';
import { useGameStore } from '../../stores/gameStore';
import * as THREE from 'three';

interface EditorObject {
  id: string;
  name: string;
  type: 'cube' | 'sphere' | 'cylinder' | 'plane' | 'spawn' | 'light' | 'trigger';
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  color: number;
  visible: boolean;
  locked: boolean;
}

export function EditorScreen() {
  const setScreen = useGameStore(s => s.setScreen);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [objects, setObjects] = useState<EditorObject[]>([
    { id: 'floor', name: 'Floor', type: 'plane', position: { x: 0, y: 0, z: 0 }, rotation: { x: -Math.PI / 2, y: 0, z: 0 }, scale: { x: 100, y: 100, z: 1 }, color: 0x2a2e3d, visible: true, locked: true },
    { id: 'spawn1', name: 'Spawn Point 1', type: 'spawn', position: { x: -10, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: { x: 1, y: 1, z: 1 }, color: 0x00e676, visible: true, locked: false },
  ]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<'select' | 'move' | 'rotate' | 'scale' | 'place'>('select');
  const [activeTab, setActiveTab] = useState<'hierarchy' | 'assets' | 'properties'>('hierarchy');
  const [sceneName, setSceneName] = useState('Untitled Map');
  const [undoStack, setUndoStack] = useState<EditorObject[][]>([]);
  const [redoStack, setRedoStack] = useState<EditorObject[][]>([]);

  const selectedObject = objects.find(o => o.id === selectedId);

  const pushUndo = () => {
    setUndoStack(prev => [...prev.slice(-50), [...objects]]);
    setRedoStack([]);
  };

  const undo = () => {
    if (undoStack.length === 0) return;
    setRedoStack(prev => [...prev, [...objects]]);
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(s => s.slice(0, -1));
    setObjects(prev);
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    setUndoStack(prev => [...prev, [...objects]]);
    const next = redoStack[redoStack.length - 1];
    setRedoStack(s => s.slice(0, -1));
    setObjects(next);
  };

  const addObject = (type: EditorObject['type']) => {
    pushUndo();
    const newObj: EditorObject = {
      id: `obj_${Date.now()}`,
      name: `${type.charAt(0).toUpperCase() + type.slice(1)} ${objects.length}`,
      type,
      position: { x: 0, y: type === 'plane' ? 0 : 1, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: type === 'plane' ? 100 : type === 'spawn' ? 1 : 3, y: type === 'plane' ? 100 : type === 'spawn' ? 1 : 3, z: type === 'plane' ? 1 : type === 'spawn' ? 1 : 3 },
      color: type === 'spawn' ? 0x00e676 : type === 'light' ? 0xffc400 : type === 'trigger' ? 0x7c4dff : 0x536dfe,
      visible: true,
      locked: false,
    };
    setObjects(prev => [...prev, newObj]);
    setSelectedId(newObj.id);
  };

  const deleteObject = () => {
    if (!selectedId) return;
    pushUndo();
    setObjects(prev => prev.filter(o => o.id !== selectedId));
    setSelectedId(null);
  };

  const duplicateObject = () => {
    if (!selectedObject) return;
    pushUndo();
    const dup = {
      ...selectedObject,
      id: `obj_${Date.now()}`,
      name: `${selectedObject.name} Copy`,
      position: { ...selectedObject.position, x: selectedObject.position.x + 2 },
    };
    setObjects(prev => [...prev, dup]);
    setSelectedId(dup.id);
  };

  const updateObject = (id: string, updates: Partial<EditorObject>) => {
    pushUndo();
    setObjects(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
  };

  const tools = [
    { id: 'select' as const, icon: '↖', label: 'Select', key: 'Q' },
    { id: 'move' as const, icon: '✥', label: 'Move', key: 'W' },
    { id: 'rotate' as const, icon: '↻', label: 'Rotate', key: 'E' },
    { id: 'scale' as const, icon: '↔', label: 'Scale', key: 'R' },
  ];

  const addObjectMenu = [
    { type: 'cube' as const, label: 'Cube', icon: '⬛' },
    { type: 'sphere' as const, label: 'Sphere', icon: '⬤' },
    { type: 'cylinder' as const, label: 'Cylinder', icon: '⏸' },
    { type: 'spawn' as const, label: 'Spawn Point', icon: '📍' },
    { type: 'light' as const, label: 'Light', icon: '💡' },
    { type: 'trigger' as const, label: 'Trigger Zone', icon: '⚡' },
  ];

  return (
    <div className="w-full h-full flex flex-col bg-cf-black">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-cf-graphite border-b border-cf-border">
        <div className="flex items-center gap-3">
          <button onClick={() => setScreen('menu')} className="cf-btn-secondary text-xs px-3 py-1">← Menu</button>
          <div className="w-px h-6 bg-cf-border" />
          <input
            type="text"
            value={sceneName}
            onChange={(e) => setSceneName(e.target.value)}
            className="bg-transparent text-sm font-semibold text-cf-white outline-none border-b border-transparent hover:border-cf-border focus:border-cf-cyan"
          />
        </div>

        <div className="flex items-center gap-1">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              title={`${tool.label} (${tool.key})`}
              className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-all ${
                activeTool === tool.id
                  ? 'bg-cf-cyan/20 text-cf-cyan border border-cf-cyan/30'
                  : 'text-cf-muted hover:text-cf-text hover:bg-cf-dark'
              }`}
            >
              {tool.icon}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button onClick={undo} className="w-8 h-8 rounded-lg flex items-center justify-center text-cf-muted hover:text-cf-text hover:bg-cf-dark" title="Undo (Ctrl+Z)">↶</button>
          <button onClick={redo} className="w-8 h-8 rounded-lg flex items-center justify-center text-cf-muted hover:text-cf-text hover:bg-cf-dark" title="Redo (Ctrl+Y)">↷</button>
          <div className="w-px h-6 bg-cf-border" />
          <button onClick={() => addObject('cube')} className="cf-btn-secondary text-xs px-3 py-1">+ Add Object</button>
          <button className="cf-btn-secondary text-xs px-3 py-1">▶ Test Play</button>
          <button className="cf-btn-primary text-xs px-3 py-1">Publish</button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Hierarchy */}
        <div className="w-56 border-r border-cf-border flex flex-col">
          <div className="flex border-b border-cf-border">
            {['hierarchy', 'assets'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as typeof activeTab)}
                className={`flex-1 py-2 text-xs font-semibold capitalize transition-all ${
                  activeTab === tab ? 'text-cf-cyan border-b-2 border-cf-cyan' : 'text-cf-muted hover:text-cf-text'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {activeTab === 'hierarchy' && objects.map((obj) => (
              <button
                key={obj.id}
                onClick={() => setSelectedId(obj.id)}
                className={`w-full text-left px-3 py-1.5 rounded-lg text-xs flex items-center gap-2 transition-all ${
                  selectedId === obj.id
                    ? 'bg-cf-cyan/10 text-cf-cyan'
                    : 'text-cf-text hover:bg-cf-dark'
                }`}
              >
                <span className="text-[10px] opacity-50">{obj.visible ? '👁' : '👁‍🗨'}</span>
                <span className={`w-2 h-2 rounded-sm`} style={{ backgroundColor: `#${obj.color.toString(16).padStart(6, '0')}` }} />
                <span className="flex-1 truncate">{obj.name}</span>
                {obj.locked && <span className="text-[10px] opacity-30">🔒</span>}
              </button>
            ))}

            {activeTab === 'assets' && (
              <div className="text-center py-8 text-cf-muted text-xs">
                <div className="text-2xl mb-2">📁</div>
                <p>Asset library empty</p>
                <p className="mt-1">Import models, textures, and sounds</p>
              </div>
            )}
          </div>
        </div>

        {/* Viewport */}
        <div ref={containerRef} className="flex-1 relative bg-cf-dark">
          <canvas ref={canvasRef} className="w-full h-full" />
          <div className="absolute bottom-4 left-4 cf-glass px-3 py-1.5 text-xs text-cf-muted">
            {activeTool.toUpperCase()} — {objects.length} objects
          </div>
          <div className="absolute bottom-4 right-4 flex gap-2">
            <div className="cf-glass px-3 py-1.5 text-xs text-cf-muted">
              LMB: Select / Move
            </div>
            <div className="cf-glass px-3 py-1.5 text-xs text-cf-muted">
              RMB: Orbit Camera
            </div>
          </div>

          {/* Quick add dropdown */}
          <div className="absolute top-4 left-4">
            <div className="cf-glass p-2 flex gap-1">
              {addObjectMenu.map((item) => (
                <button
                  key={item.type}
                  onClick={() => addObject(item.type)}
                  title={`Add ${item.label}`}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-sm hover:bg-cf-dark transition-all"
                >
                  {item.icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel - Properties */}
        <div className="w-64 border-l border-cf-border overflow-y-auto">
          <div className="p-3 border-b border-cf-border">
            <span className="text-xs font-semibold text-cf-white uppercase tracking-wider">Properties</span>
          </div>

          {selectedObject ? (
            <div className="p-3 space-y-4">
              <div>
                <label className="text-[10px] text-cf-muted uppercase">Name</label>
                <input
                  type="text"
                  value={selectedObject.name}
                  onChange={(e) => updateObject(selectedObject.id, { name: e.target.value })}
                  className="cf-input mt-1 text-xs"
                />
              </div>

              <div>
                <label className="text-[10px] text-cf-muted uppercase">Type</label>
                <div className="cf-input mt-1 text-xs text-cf-muted capitalize">{selectedObject.type}</div>
              </div>

              <div>
                <label className="text-[10px] text-cf-muted uppercase">Position</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {(['x', 'y', 'z'] as const).map((axis) => (
                    <div key={axis}>
                      <span className="text-[10px] text-cf-muted uppercase">{axis}</span>
                      <input
                        type="number"
                        value={selectedObject.position[axis]}
                        onChange={(e) => {
                          const pos = { ...selectedObject.position, [axis]: parseFloat(e.target.value) || 0 };
                          updateObject(selectedObject.id, { position: pos });
                        }}
                        className="cf-input text-xs font-mono mt-0.5"
                        step="0.5"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] text-cf-muted uppercase">Rotation</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {(['x', 'y', 'z'] as const).map((axis) => (
                    <div key={axis}>
                      <span className="text-[10px] text-cf-muted uppercase">{axis}</span>
                      <input
                        type="number"
                        value={(selectedObject.rotation[axis] * 180 / Math.PI).toFixed(1)}
                        onChange={(e) => {
                          const rot = { ...selectedObject.rotation, [axis]: (parseFloat(e.target.value) || 0) * Math.PI / 180 };
                          updateObject(selectedObject.id, { rotation: rot });
                        }}
                        className="cf-input text-xs font-mono mt-0.5"
                        step="15"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] text-cf-muted uppercase">Scale</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {(['x', 'y', 'z'] as const).map((axis) => (
                    <div key={axis}>
                      <span className="text-[10px] text-cf-muted uppercase">{axis}</span>
                      <input
                        type="number"
                        value={selectedObject.scale[axis]}
                        onChange={(e) => {
                          const sc = { ...selectedObject.scale, [axis]: parseFloat(e.target.value) || 1 };
                          updateObject(selectedObject.id, { scale: sc });
                        }}
                        className="cf-input text-xs font-mono mt-0.5"
                        step="0.5"
                        min="0.1"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] text-cf-muted uppercase">Color</label>
                <input
                  type="color"
                  value={`#${selectedObject.color.toString(16).padStart(6, '0')}`}
                  onChange={(e) => updateObject(selectedObject.id, { color: parseInt(e.target.value.slice(1), 16) })}
                  className="w-full h-8 rounded-lg bg-cf-dark border border-cf-border cursor-pointer mt-1"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={duplicateObject} className="cf-btn-secondary text-xs flex-1">Duplicate</button>
                <button onClick={deleteObject} className="cf-btn-danger text-xs flex-1">Delete</button>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center text-cf-muted text-xs">
              <div className="text-3xl mb-2 opacity-30">🔍</div>
              <p>Select an object to view properties</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
